import type { ConnectDropTarget } from '@nosferatu500/react-dnd'
import React, { type JSX, type ReactNode } from 'react'
import type { TreeItem, UnknownNodeData } from './types'
import { classnames } from './utils/classnames'
import type { FlatDataItem } from './utils/tree-data-utils'
import './tree-node.css'

export interface TreeRendererProps<TData = UnknownNodeData> {
  treeIndex: number
  treeId: string
  swapFrom?: number
  swapDepth?: number
  swapLength?: number
  scaffoldBlockPxWidth: number
  lowerSiblingCounts: number[]
  rowDirection: 'ltr' | 'rtl' | string | undefined
  rowHeight:
    | number
    | ((treeIndex: number, node: TreeItem<TData>, path: number[]) => number)

  listIndex: number
  children: JSX.Element[]
  style?: React.CSSProperties

  // ARIA tree semantics, supplied by the tree because a virtualized list has
  // no DOM nesting to infer depth or sibling position from. Forwarded to the
  // row element through `otherProps`.
  role?: string
  tabIndex?: number
  'aria-level'?: number
  'aria-setsize'?: number
  'aria-posinset'?: number
  'aria-expanded'?: boolean

  // Drop target
  connectDropTarget: ConnectDropTarget
  isOver: boolean
  canDrop?: boolean
  draggedNode?: TreeItem<TData>

  // used in dndManager
  getPrevRow: () => FlatDataItem<TData> | undefined
  node: TreeItem<TData>
  path: number[]
}

const getLineClass = (
  lowerSiblingCount: number,
  listIndex: number,
  i: number,
  scaffoldBlockCount: number
): string => {
  if (lowerSiblingCount > 0) {
    if (listIndex === 0)
      // Top-left corner of the tree
      // +-----+
      // |     |
      // |  +--+
      // |  |  |
      // +--+--+
      return 'rst__lineHalfHorizontalRight rst__lineHalfVerticalBottom'
    if (i === scaffoldBlockCount - 1)
      // Last scaffold block in the row, right before the row content
      // +--+--+
      // |  |  |
      // |  +--+
      // |  |  |
      // +--+--+
      return 'rst__lineHalfHorizontalRight rst__lineFullVertical'
    // Simply connecting the line extending down to the next sibling on this level
    // +--+--+
    // |  |  |
    // |  |  |
    // |  |  |
    // +--+--+
    return 'rst__lineFullVertical'
  }
  // Top-left corner of the tree, but has no siblings
  // +-----+
  // |     |
  // |  +--+
  // |     |
  // +-----+
  if (listIndex === 0) return 'rst__lineHalfHorizontalRight'
  if (i === scaffoldBlockCount - 1)
    // The last or only node in this level of the tree
    // +--+--+
    // |  |  |
    // |  +--+
    // |     |
    // +-----+
    return 'rst__lineHalfVerticalTop rst__lineHalfHorizontalRight'
  return ''
}

const TreeNodeComponent = <TData,>({
  children,
  listIndex,
  swapFrom = undefined,
  swapLength = undefined,
  swapDepth = undefined,
  scaffoldBlockPxWidth,
  lowerSiblingCounts,
  connectDropTarget,
  /*
   * Accepted and unused. These reach the content renderer through
   * `RowDropContext`, provided by `wrapTarget` — see `utils/row-drop-state.ts`.
   * They stay in the props because a custom `treeNodeRenderer` may want them for
   * its own styling, and because the wrapper passes them either way.
   *
   * `draggedNode` has never been supplied by anything: the tree does not pass it
   * down, so it was `undefined` in every `cloneElement` too. Wiring it up would
   * switch on the `isDraggedDescendant` dimming in both node renderers, which is
   * a visual change rather than part of this refactor.
   */
  isOver: _isOver,
  draggedNode: _draggedNode = undefined,
  canDrop: _canDrop = false,
  treeIndex,
  rowHeight,
  rowDirection = 'ltr',
  // Extract props not used in DOM or needed by children directly
  treeId: _treeId,
  getPrevRow: _getPrevRow,
  node,
  path,
  ...otherProps
}: TreeRendererProps<TData>): React.JSX.Element => {
  const rowDirectionClass = rowDirection === 'rtl' ? 'rst__rtl' : undefined

  // Construct the scaffold representing the structure of the tree
  const scaffoldBlockCount = lowerSiblingCounts.length
  const scaffold: ReactNode[] = []

  for (const [i, lowerSiblingCount] of lowerSiblingCounts.entries()) {
    const lineClass = getLineClass(
      lowerSiblingCount,
      listIndex,
      i,
      scaffoldBlockCount
    )

    scaffold.push(
      <div
        key={`pre_${1 + i}`}
        style={{ width: scaffoldBlockPxWidth }}
        className={classnames(
          'rst__lineBlock',
          lineClass,
          rowDirectionClass ?? ''
        )}
      />
    )

    if (treeIndex !== listIndex && i === swapDepth) {
      // This row has been shifted, and is at the depth of
      // the line pointing to the new destination
      let highlightLineClass: string
      if (listIndex === swapFrom! + swapLength! - 1) {
        // This block is on the bottom (target) line
        // This block points at the target block (where the row will go when released)
        highlightLineClass = 'rst__highlightBottomLeftCorner'
      } else if (treeIndex === swapFrom) {
        // This block is on the top (source) line
        highlightLineClass = 'rst__highlightTopLeftCorner'
      } else {
        // This block is between the bottom and top
        highlightLineClass = 'rst__highlightLineVertical'
      }

      const style =
        rowDirection === 'rtl'
          ? {
              width: scaffoldBlockPxWidth,
              right: scaffoldBlockPxWidth * i,
            }
          : {
              width: scaffoldBlockPxWidth,
              left: scaffoldBlockPxWidth * i,
            }

      scaffold.push(
        <div
          key={i}
          style={style}
          className={classnames(
            'rst__absoluteLineBlock',
            highlightLineClass,
            rowDirectionClass ?? ''
          )}
        />
      )
    }
  }

  const contentStyle =
    rowDirection === 'rtl'
      ? { right: scaffoldBlockPxWidth * scaffoldBlockCount }
      : { left: scaffoldBlockPxWidth * scaffoldBlockCount }

  const calculatedRowHeight =
    typeof rowHeight === 'function'
      ? rowHeight(treeIndex, node, path)
      : rowHeight

  return (
    <div
      {...otherProps}
      ref={connectDropTarget}
      style={{ height: `${calculatedRowHeight}px` }}
      className={classnames('rst__node', rowDirectionClass ?? '')}>
      {scaffold}

      <div className="rst__nodeContent" style={contentStyle}>
        {children}
      </div>
    </div>
  )
}

/**
 * Deliberately not wrapped in `React.memo` — the memo boundary is `TreeRow` in
 * `react-sortable-tree.tsx`, one level up.
 *
 * A memo here cannot work and was measured not working: a row renders as
 * `<TreeNodeRenderer …><NodeContentRenderer …/></…>`, so this component's
 * `children` is a fresh element on every render and a shallow comparison can
 * never pass. It was pure overhead plus a misleading signal that rows were
 * memoized. `TreeRow` builds that composition *inside* itself instead, which puts
 * the freshness inside the boundary where it costs nothing. Do not add a memo
 * here to "help"; add props to `TreeRow` carefully instead, and keep them values
 * or stable references.
 */
export default TreeNodeComponent
